import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send } from "lucide-react";

export default function Inbox() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");

  const loadThreads = () => api.get("/messages/threads").then(({ data }) => setThreads(data.threads || []));

  useEffect(() => { loadThreads(); }, []);

  useEffect(() => {
    if (!active) return;
    api.get(`/messages/thread/${active.thread_key}`).then(({ data }) => setMessages(data.messages || []));
  }, [active]);

  const send = async () => {
    if (!active || !text.trim()) return;
    try {
      await api.post("/messages", {
        listing_id: active.listing_id || null,
        recipient_id: active.other_id,
        body: text.trim(),
      });
      setText("");
      const { data } = await api.get(`/messages/thread/${active.thread_key}`);
      setMessages(data.messages || []);
      loadThreads();
    } catch (e) {
      toast.error("Failed to send");
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-10">
      <div className="overline text-safety">Messages</div>
      <h1 className="mt-2 font-display font-black text-4xl tracking-tighter text-forest">Inbox</h1>

      <div className="mt-8 border border-border bg-white grid grid-cols-1 md:grid-cols-[320px,1fr] min-h-[520px]">
        <aside className="border-r border-border">
          {threads.length === 0 ? (
            <div className="p-8 text-sm text-muted-foreground text-center">No conversations yet.</div>
          ) : threads.map((t) => (
            <button
              key={t.thread_key}
              onClick={() => setActive(t)}
              data-testid={`inbox-thread-${t.thread_key}`}
              className={`w-full text-left border-b border-border p-4 hover:bg-sage-light transition-colors duration-200 ${active?.thread_key === t.thread_key ? "bg-sage-light" : ""}`}
            >
              <div className="font-display text-base tracking-tight truncate">{t.other_name}</div>
              <div className="mt-1 text-sm text-muted-foreground line-clamp-1">{t.last_body}</div>
              <div className="mt-1 overline text-[10px] text-muted-foreground">{new Date(t.last_at).toLocaleString("en-GB")}</div>
            </button>
          ))}
        </aside>
        <section className="flex flex-col">
          {!active ? (
            <div className="flex-1 grid place-items-center text-sm text-muted-foreground">Select a conversation</div>
          ) : (
            <>
              <div className="border-b border-border p-4">
                <div className="font-display text-lg tracking-tight">{active.other_name}</div>
                {active.listing_id && <div className="overline text-[10px] text-muted-foreground mt-1">About listing #{active.listing_id.slice(-6)}</div>}
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.map((m) => {
                  const mine = m.sender_id === user.user_id;
                  return (
                    <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[70%] p-3 text-sm ${mine ? "bg-forest text-white" : "bg-sage border border-border"}`}>
                        <div className="leading-relaxed whitespace-pre-line">{m.body}</div>
                        <div className={`mt-1 text-[10px] overline ${mine ? "text-white/70" : "text-muted-foreground"}`}>
                          {new Date(m.created_at).toLocaleString("en-GB")}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="border-t border-border p-3 flex gap-2">
                <Input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Type a message…"
                  data-testid="inbox-input"
                  className="rounded-none h-11"
                />
                <Button onClick={send} data-testid="inbox-send" className="rounded-none bg-forest hover:bg-forest-800 text-white h-11">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
